import { cn } from '@nextui-org/react'
import { useState } from 'react'
import DiffEditor, { DiffEditorProps } from '~/components/editors/monaco/DiffEditor'
import FileList from '~/components/editors/monaco/FileList'
import {
  useConversationMetadata,
  useConversationUuid,
  useIsInternalUploadedIndex,
  useIsLocalSyncIndex
} from '~/hooks'
import { useIsS3Task } from '~/hooks/task/useS3Task'
import { useTaskBlock } from '~/hooks/task/useTaskBlock'
import { CommandType, Diff } from '~/protos/agents'
import { applyDiff } from '~/services/taskService'
import { TabOption } from '~/types/TabOption'
import { AppEvents, globalEventEmitter } from '~/utils/shared'
import { BetterToast } from '~/utils/toast'
import { TaskBlock, TaskBlockProps } from './blocks'

interface CodeEditorProps {
  initialDiffs: Diff[] | null
  readOnly?: boolean
}

function CodeEditor({ initialDiffs, readOnly = false }: CodeEditorProps): JSX.Element {
  const uuid = useConversationUuid()
  const [diffs, setDiffs] = useState<Diff[] | null>(initialDiffs ?? null)
  const [activeDiff, setActiveDiff] = useState<Diff | null>(diffs?.[0] ?? null)
  const isS3Task = useIsS3Task()
  const taskBlock = useTaskBlock()
  const isInternalUploadedIndex = useIsInternalUploadedIndex()
  const isLocalSyncIndex = useIsLocalSyncIndex()
  const { command } = useConversationMetadata()
  const isMulesoftToCamel = command === CommandType.MULESOFT_TO_CAMEL_MIGRATION
  const isReadOnly =
    isS3Task || readOnly || isInternalUploadedIndex || isLocalSyncIndex || isMulesoftToCamel

  const onContentChange: DiffEditorProps['onContentChange'] = ({ fileContent }) => {
    setDiffs(
      diffs?.map(diff => (diff === activeDiff ? { ...diff, bFileContent: fileContent } : diff)) ??
        null
    )
  }

  const onAcceptDiff = async () => {
    if (Array.isArray(diffs)) {
      await applyDiff({ uuid, diffs })
      BetterToast.success('Diff applied')
      globalEventEmitter.emit(AppEvents.setActiveTab, TabOption.HELP)
    }
  }

  return (
    <div className={cn('flex flex-col w-full', isReadOnly ? '' : 'gap-10')}>
      {isReadOnly && taskBlock ? (
        <TaskBlock fields={taskBlock.fields as TaskBlockProps['fields']} children={[]} />
      ) : null}
      <FileList
        diffs={diffs}
        activeDiff={activeDiff}
        onSelectDiff={setActiveDiff}
        onAcceptDiff={onAcceptDiff}
        readOnly={isReadOnly}
        fileListsOnly={isReadOnly}
      />

      <DiffEditor
        key={activeDiff?.bFileContent}
        diff={activeDiff}
        onContentChange={onContentChange}
        readOnly={isReadOnly}
      />
    </div>
  )
}

export default CodeEditor
